import { getCurrentSession } from "./cognito";
import { emitApiError } from "@/contexts/ApiErrorContext";

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.taxlienvest.com";

/**
 * Get the access token for API requests
 */
const getAccessToken = async (): Promise<string | null> => {
	try {
		const session = await getCurrentSession();
		const accessToken = session.getAccessToken();
		return accessToken.getJwtToken();
	} catch (error) {
		console.error("Failed to get access token:", error);
		return null;
	}
};

const handleApiFailure = async (
	response: Response,
	endpoint: string,
	fallbackMessage: string
) => {
	const errorData = await response.json().catch(() => ({}));
	const message =
		errorData.message || `${fallbackMessage}: ${response.statusText}`;

	// emitApiError manages Client Context state, so it can only be called on the client.
	// When running on the server (SSR), we just throw the error to be caught by the server component.
	if (typeof window !== "undefined") {
		emitApiError({
			status: response.status,
			message,
			endpoint,
		});
	}

	throw new Error(message);
};

export interface DateFilter {
	operator?: string;
	value?: string;
}

export interface NumericalFilter {
	operator?: string;
	value?: number;
}

export interface NearbyPropertiesRequest {
	latitude?: number;
	longitude?: number;
	county?: string;
	state?: string;
	page?: number;
	pageSize?: number;
	dateFilters?: Record<string, DateFilter>;
	numericalFilters?: Record<string, NumericalFilter>;
}

// Raw API response type (can be snake_case or camelCase)
export type RawPropertyRecord = {
	id: string;
	version: number;
	// Handle both snake_case and camelCase
	property_id?: string;
	propertyId?: string;
	parcel_data?: {
		[key: string]: unknown;
	} | null;
	parcelData?: {
		[key: string]: unknown;
	} | null;
	scraped_parcel_data?: {
		[key: string]: unknown;
	} | null;
	scrapedParcelData?: {
		[key: string]: unknown;
	} | null;
	geo_location?: {
		lat: number;
		lon: number;
	};
	latitude?: number;
	longitude?: number;
	county: string;
	state: string;
	zip?: string | null;
	country: string;
	address: string;
	region: string;
	district: string;
	legal_record?: {
		file_number?: string | null;
		plantiff?: string | null;
		attorney?: string | null;
		defendant?: string | null;
		[key: string]: unknown;
	} | null;
	additional_details_record?: {
		property_url?: string | null;
		link?: string | null;
		description?: string | null;
		[key: string]: unknown;
	} | null;
	land_record?: {
		parcel_no?: string | null;
		real_id?: string | null;
		[key: string]: unknown;
	} | null;
	legalCaseNumber?: string | null;
	sheriffFile?: string | null;
	plaintiff?: string | null;
	attorneyFirm?: string | null;
	defendant?: string | null;
	propertyLink?: string | null;
	civilViewLink?: string | null;
	description?: string | null;
	landRecordId?: string | null;
	landRecordRealId?: string | null;
	// Hanover County specific fields
	landRecordParcelNo?: string | null;
	taxRecordId?: string | null;
	taxBid?: number | null;
	ownerRecordId?: string | null;
	owner?: string | null;
	additionalDetailsLink?: string | null;
	_displayNames?: {
		[key: string]: string | undefined;
	};
	// Additional backend fields
	[key: string]: unknown;
};

export interface NearbyPropertiesResponse {
	data: Array<RawPropertyRecord>;
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

// Normalized property record type (always camelCase with _displayNames)
export type NormalizedPropertyRecord = {
	id: string;
	version: number;
	propertyId: string;
	parcelData: {
		[key: string]: unknown;
	} | null;
	scrapedParcelData?: {
		[key: string]: unknown;
	} | null;
	latitude: number;
	longitude: number;
	county: string;
	state: string;
	zip: string;
	country: string;
	address: string;
	region: string;
	district: string;
	legalCaseNumber?: string | null;
	sheriffFile?: string | null;
	plaintiff?: string | null;
	attorneyFirm?: string | null;
	defendant?: string | null;
	propertyLink?: string | null;
	civilViewLink?: string | null;
	description?: string | null;
	landRecordId?: string | null;
	landRecordRealId?: string | null;
	// Hanover County specific fields
	landRecordParcelNo?: string | null;
	taxRecordId?: string | null;
	taxBid?: number | null;
	ownerRecordId?: string | null;
	owner?: string | null;
	additionalDetailsLink?: string | null;
	// URL fields from parcel data
	taxBillUrl?: string | null;
	viewMapUrl?: string | null;
	recentSalesUrl?: string | null;
	codeViolationFeeUrl?: string | null;
	taxAndSewerOnlyUrl?: string | null;
	elevatorCertificateUrl?: string | null;
	codeViolationDetailsUrl?: string | null;
	propertyCharacteristicsUrl?: string | null;
	constructionViolationDetailsUrl?: string | null;
	auctionDate?: string | null;
	_displayNames: {
		[key: string]: string | undefined;
	};
	medias?: string[];
	[key: string]: unknown;
};

/**
 * Normalize raw API response to consistent format
 * Handles both snake_case (Wayne County) and camelCase formats
 */
export function normalizePropertyRecord(
	raw: RawPropertyRecord
): NormalizedPropertyRecord {
	// Extract property ID (handle both formats)
	const propertyId = raw.propertyId || raw.property_id || raw.id;

	// Extract parcel data (handle both formats)
	const parcelData = raw.parcelData || raw.parcel_data || null;

	// Extract scraped parcel data (handle both formats)
	const scrapedParcelData =
		raw.scrapedParcelData || raw.scraped_parcel_data || null;

	// Extract coordinates (handle both formats)
	const latitude = raw.latitude ?? raw.geo_location?.lat ?? 0;
	const longitude = raw.longitude ?? raw.geo_location?.lon ?? 0;

	// Extract legal record fields
	const legalRecord = raw.legal_record;
	const legalCaseNumber =
		raw.legalCaseNumber || legalRecord?.file_number || null;
	const plaintiff = raw.plaintiff || legalRecord?.plantiff || null;
	const attorneyFirm = raw.attorneyFirm || legalRecord?.attorney || null;
	const defendant = raw.defendant || legalRecord?.defendant || null;

	// Extract additional details
	const additionalDetails = raw.additional_details_record;
	const propertyLink =
		raw.propertyLink ||
		raw.additionalDetailsLink ||
		additionalDetails?.property_url ||
		additionalDetails?.link ||
		null;
	const description =
		raw.description || additionalDetails?.description || null;

	// Extract land record
	const landRecord = raw.land_record;
	const landRecordId =
		raw.landRecordId ||
		raw.land_record_id ||
		(typeof landRecord?.id === "string" ? landRecord.id : null) ||
		null;
	const landRecordRealId =
		raw.landRecordRealId ||
		raw.land_record_real_id ||
		(typeof landRecord?.real_id === "string" ? landRecord.real_id : null) ||
		null;
	const landRecordParcelNo =
		raw.landRecordParcelNo ||
		(typeof landRecord?.parcel_no === "string"
			? landRecord.parcel_no
			: null) ||
		null;

	// Generate default display names if not provided
	const defaultDisplayNames: Record<string, string> = {
		id: "ID",
		version: "Version",
		propertyId: "Property ID",
		parcelData: "Parcel Data",
		scrapedParcelData: "Scraped Parcel Data",
		latitude: "Latitude",
		longitude: "Longitude",
		county: "County",
		state: "State",
		zip: "ZIP Code",
		country: "Country",
		address: "Address",
		region: "Region",
		district: "District",
		legalCaseNumber: "Legal Case Number",
		sheriffFile: "Sheriff File",
		plaintiff: "Plaintiff",
		attorneyFirm: "Attorney Firm",
		defendant: "Defendant",
		propertyLink: "Property Link",
		civilViewLink: "Civil View Link",
		description: "Description",
		landRecordId: "Land Record ID",
		landRecordRealId: "Land Record Real ID",
		landRecordParcelNo: "Land Record Parcel Number",
		taxRecordId: "Tax Record ID",
		taxBid: "Tax Bid",
		ownerRecordId: "Owner Record ID",
		owner: "Owner",
		additionalDetailsLink: "Additional Details Link",
		taxBillUrl: "Tax Bill URL",
		viewMapUrl: "View Map URL",
		recentSalesUrl: "Recent Sales URL",
		codeViolationFeeUrl: "Code Violation Fee URL",
		taxAndSewerOnlyUrl: "Tax and Sewer Only URL",
		elevatorCertificateUrl: "Elevator Certificate URL",
		codeViolationDetailsUrl: "Code Violation Details URL",
		propertyCharacteristicsUrl: "Property Characteristics URL",
		constructionViolationDetailsUrl: "Construction Violation Details URL",
		auctionDate: "Auction Date",
	};

	// Use provided display names or defaults
	const displayNames = raw._displayNames || defaultDisplayNames;

	// Build normalized record (explicitly set fields to avoid overwriting)
	const normalized: NormalizedPropertyRecord = {
		id: raw.id,
		version: raw.version,
		propertyId: String(propertyId),
		parcelData,
		scrapedParcelData,
		latitude: Number(latitude),
		longitude: Number(longitude),
		county: raw.county,
		state: raw.state,
		zip: raw.zip || "",
		country: raw.country,
		address: raw.address,
		region: raw.region,
		district: raw.district || "",
		legalCaseNumber,
		sheriffFile: raw.sheriffFile || null,
		plaintiff,
		attorneyFirm,
		defendant,
		propertyLink,
		civilViewLink: raw.civilViewLink || null,
		description,
		landRecordId: typeof landRecordId === "string" ? landRecordId : null,
		landRecordRealId:
			typeof landRecordRealId === "string" ? landRecordRealId : null,
		landRecordParcelNo:
			raw.landRecordParcelNo ||
			(typeof landRecordParcelNo === "string"
				? landRecordParcelNo
				: null) ||
			null,
		taxRecordId: raw.taxRecordId || null,
		taxBid: raw.taxBid || null,
		ownerRecordId: raw.ownerRecordId || null,
		owner: raw.owner || null,
		additionalDetailsLink: raw.additionalDetailsLink || null,
		_displayNames: displayNames,
	};

	// Preserve all other fields from raw (but don't overwrite normalized fields)
	Object.keys(raw).forEach((key) => {
		if (!(key in normalized)) {
			(normalized as Record<string, unknown>)[key] =
				raw[key as keyof typeof raw];
		}
	});

	return normalized;
}

/**
 * Fetch nearby real estate properties
 */
export const fetchNearbyProperties = async (
	request: NearbyPropertiesRequest
): Promise<NearbyPropertiesResponse> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const requestBody: Record<string, unknown> = {
		page: request.page || 1,
		pageSize: request.pageSize || 20,
	};

	// Include latitude/longitude if provided
	if (request.latitude !== undefined && request.longitude !== undefined) {
		requestBody.latitude = request.latitude;
		requestBody.longitude = request.longitude;
	}

	// Include county/state if provided
	if (request.county) {
		requestBody.county = request.county;
	}
	if (request.state) {
		requestBody.state = request.state;
	}

	// Include date filters if provided
	if (request.dateFilters && Object.keys(request.dateFilters).length > 0) {
		requestBody.dateFilters = request.dateFilters;
	}

	// Include numerical filters if provided
	if (
		request.numericalFilters &&
		Object.keys(request.numericalFilters).length > 0
	) {
		requestBody.numericalFilters = request.numericalFilters;
	}

	const response = await fetch(`${API_BASE_URL}/real-estate/nearby`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(requestBody),
	});

	if (!response.ok) {
		await handleApiFailure(
			response,
			"/real-estate/nearby",
			"Failed to fetch properties"
		);
	}

	return response.json();
};

/**
 * Fetch nearby real estate properties (unauthenticated - for playground)
 * Returns only 10 properties with no pagination
 */
export const fetchNearbyPropertiesUnauthenticated = async (
	request: Pick<
		NearbyPropertiesRequest,
		"latitude" | "longitude" | "county" | "state"
	>
): Promise<NearbyPropertiesResponse> => {
	const requestBody: Record<string, unknown> = {
		page: 1,
		pageSize: 10, // Limit to 10 for playground
	};

	// Include latitude/longitude if provided
	if (request.latitude !== undefined && request.longitude !== undefined) {
		requestBody.latitude = request.latitude;
		requestBody.longitude = request.longitude;
	}

	// Include county/state if provided
	if (request.county) {
		requestBody.county = request.county;
	}
	if (request.state) {
		requestBody.state = request.state;
	}

	const response = await fetch(`${API_BASE_URL}/real-estate/nearby-public`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			// No Authorization header for unauthenticated requests
		},
		body: JSON.stringify(requestBody),
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		const message =
			errorData.message ||
			`Failed to fetch properties: ${response.statusText}`;
		throw new Error(message);
	}

	return response.json();
};

/**
 * Fetch property details by ID
 */
export const getPropertyById = async (
	id: string
): Promise<NormalizedPropertyRecord> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(`${API_BASE_URL}/real-estate/${id}`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		await handleApiFailure(
			response,
			`/real-estate/${id}`,
			"Failed to fetch property details"
		);
	}

	return response.json();
};

/**
 * Fetch property details by ID (with auth header)
 * @param id - Property ID
 * @param token - Optional access token (for server-side usage)
 */
export const getPropertyByIdPublic = async (
	id: string,
	token?: string | null
): Promise<NormalizedPropertyRecord> => {
	// Try to get token if not provided (client-side)
	let accessToken = token;
	if (!accessToken && typeof window !== "undefined") {
		try {
			accessToken = await getAccessToken();
		} catch {
			// unauthenticated request
		}
	}

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};
	if (accessToken) {
		headers.Authorization = `Bearer ${accessToken}`;
	}

	const requestUrl = `${API_BASE_URL}/real-estate/${id}`;

	try {
		const response = await fetch(requestUrl, {
			method: "GET",
			headers,
			cache: "default",
		});

		if (!response.ok) {
			await handleApiFailure(
				response,
				`/real-estate/${id}`,
				"Failed to fetch property details"
			);
		}

		const text = await response.text();
		if (!text || !text.trim()) {
			throw new Error("Empty response from property API");
		}
		try {
			const data = JSON.parse(text) as NormalizedPropertyRecord;
			return data;
		} catch (parseError) {
			const msg =
				parseError instanceof Error
					? parseError.message
					: "Invalid JSON";
			throw new Error(`Invalid JSON from property API: ${msg}`);
		}
	} catch (error) {
		const msg = error instanceof Error ? error.message : String(error);
		const cause =
			error instanceof Error && "cause" in error
				? (error as Error & { cause?: { code?: string } }).cause
				: null;
		const code =
			cause && typeof cause === "object" && "code" in cause
				? (cause as { code: string }).code
				: "";
		// Empty or invalid JSON is often a missing property; log as warning to avoid noisy errors
		const isEmptyOrInvalid =
			msg.includes("Empty response") || msg.includes("Invalid JSON");
		if (isEmptyOrInvalid) {
			console.warn(`[getPropertyByIdPublic] /real-estate/${id}: ${msg}`);
		} else {
			console.error(
				`[getPropertyByIdPublic] fetch failed for /real-estate/${id}: ${msg}${code ? ` (${code})` : ""}`
			);
		}
		throw error;
	}
};
